/* eslint no-console:0 */
/**
 * Lazily loads harper.js (https://writewithharper.com) and exposes a single shared
 * WorkerLinter for spell & grammar checking. Linting happens on-device in a web worker,
 * no text leaves the browser.
 *
 * harper.js ships as modern ESM + WebAssembly which webpack 4 / babel 6 cannot parse,
 * so its `dist` folder is served untouched from `/harper/` (see webpack configs) and
 * imported at runtime by `/harper-loader.js`, a native module script outside the bundle.
 */
import { lintWithCustomRules, mergeLints } from './grammarRules';

const LOADER_PATH = '/harper-loader.js';
const LOADED_EVENT = 'harper-loaded';
const DICTIONARY_STORAGE_KEY = 'trilogy.spellcheck.dictionary';

// harper rules that flag correct sentences, e.g. MissingPreposition flags "The hospital has many beds."
const DISABLED_RULES = ['MissingPreposition'];

let linterPromise = null;

const loadHarperModule = () =>
  new Promise((resolve, reject) => {
    if (window.harper) {
      resolve(window.harper);
      return;
    }

    const onLoaded = () => {
      window.removeEventListener(LOADED_EVENT, onLoaded);
      if (window.harper) {
        resolve(window.harper);
      } else {
        reject(new Error('harper.js failed to load'));
      }
    };
    window.addEventListener(LOADED_EVENT, onLoaded);

    const script = document.createElement('script');
    script.type = 'module';
    script.src = LOADER_PATH;
    script.onerror = onLoaded;
    document.head.appendChild(script);
  });

export const getStoredDictionary = () => {
  try {
    return (
      JSON.parse(window.localStorage.getItem(DICTIONARY_STORAGE_KEY)) || []
    );
  } catch (e) {
    return [];
  }
};

/**
 * @returns {Promise<WorkerLinter>} shared linter, created on first use
 */
export const getLinter = () => {
  if (!linterPromise) {
    linterPromise = loadHarperModule()
      .then(async harper => {
        const linter = new harper.WorkerLinter({
          binary: harper.binary,
          dialect: harper.Dialect.American
        });
        await linter.setup();
        const config = await linter.getLintConfig();
        await linter.setLintConfig(
          DISABLED_RULES.reduce(
            (updated, rule) => ({ ...updated, [rule]: false }),
            config
          )
        );
        const words = getStoredDictionary();
        if (words.length) {
          await linter.importWords(words);
        }
        return linter;
      })
      .catch(e => {
        console.error(
          '[SpellCheck] Spell & grammar check unavailable, falling back to a plain textarea',
          e
        );
        linterPromise = null;
        throw e;
      });
  }
  return linterPromise;
};

/**
 * Spell & grammar checks plain text: harper.js lints plus supplementary grammar rules.
 * Returns serializable lint objects with JS string (UTF-16) offsets.
 */
export const lintText = async text => {
  const linter = await getLinter();
  const lints = await linter.lint(text, { language: 'plaintext' });

  const harperLints = lints.map(lint => {
    const span = lint.span();
    const result = {
      start: span.start,
      end: span.end,
      kind: lint.lint_kind(),
      message: lint.message().replace(/`/g, '"'), // messages use markdown code quotes
      problemText: lint.get_problem_text(),
      suggestions: lint.suggestions().map(suggestion => {
        const value = {
          kind: suggestion.kind(),
          text: suggestion.get_replacement_text()
        };
        suggestion.free();
        return value;
      })
    };
    span.free();
    lint.free();
    return result;
  });

  return mergeLints(harperLints, lintWithCustomRules(text));
};

/**
 * Adds a word to the user's dictionary (persisted in localStorage)
 */
export const addToDictionary = async word => {
  const words = getStoredDictionary();
  if (!words.includes(word)) {
    words.push(word);
    try {
      window.localStorage.setItem(
        DICTIONARY_STORAGE_KEY,
        JSON.stringify(words)
      );
    } catch (e) {
      console.error(e);
    }
  }
  const linter = await getLinter();
  await linter.importWords([word]);
};
